<template>
  <div class="container">
    <div class="box user-info-box">
      <h3>사용자 정보</h3>
      <p>
        <strong>이름:</strong>
        {{ user?.name }}
      </p>
      <p>
        <strong>사주:</strong>
        {{ formattedGanji }}
      </p>
      <p>
        <strong>오행:</strong>
        {{ formattedElements }}
      </p>
      <p>
        <strong>체질:</strong>
        {{ user.bodyType || '정보 없음' }}
      </p>
      <p>
        <strong>Lydia 잔액:</strong>
        {{ userLydia }} LYD
      </p>
      <p>
        <strong>첫 접속일:</strong>
        {{ formattedCreatedAt }}
      </p>
      <!-- 복구 코드 입력 -->
      <div class="recovery-box">
        <input
          v-model="recoveryCode"
          type="text"
          placeholder="복구코드를 입력하세요"
        />
        <button @click="recoverAccount" :disabled="loading">복구</button>
      </div>
      <div class="message-area">
        <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>
        <p v-else-if="successMessage" class="success-message">
          {{ successMessage }}
        </p>
      </div>
    </div>
    <div class="box settings-box">
      <h3>설정</h3>
      <div class="font-size-slider">
        <label for="fontScale">
          폰트 크기 조정:
          <strong>{{ fontScaleLabel }}</strong>
        </label>
        <input
          id="fontScale"
          type="range"
          min="0.9"
          max="2"
          step="0.05"
          v-model="fontScale"
        />
      </div>

    <div class="push-toggle">
      <div class="push-header">
        <label for="pushToggle">
          오늘의 기운 알림 받기:
          <strong>{{ pushEnabled ? 'ON' : 'OFF' }}</strong>
        </label>
        <input
          id="pushToggle"
          type="checkbox"
          v-model="pushEnabled"
          @change="handlePushToggle"
        />
      </div>
      <p class="push-hint">
        (매일 오전 7시 오늘 하루의 기운 흐름을 보내드립니다.)
      </p>
    </div>

    </div>
    <div class="footer">
      <button @click="goBack" class="back-button danger">돌아가기</button>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, watch } from 'vue'
  import { useStore } from 'vuex'
  import { useRouter } from 'vue-router'
  import {
    collection,
    query,
    where,
    getDocs,
    doc,
    deleteDoc,
  } from 'firebase/firestore'
  import { db } from '@/firebase'

  // Push 관련 함수 import
  import {
    requestPermissionAndSubscribe,
    unsubscribeFromTopic,
  } from '@/utils/pushSubscribe.js'


  const recoveryCode = ref('')
  const errorMessage = ref('')
  const successMessage = ref('')

  const stored = parseFloat(localStorage.getItem('fontScale') || '')
  const fontScale = ref(isNaN(stored) ? 1 : stored)

  const fontScaleLabel = computed(
    () => `${Number(fontScale.value).toFixed(2)}x`,
  )

  watch(fontScale, (newVal) => {
    document.documentElement.style.setProperty(
      '--font-scale',
      newVal.toString(),
    )
    localStorage.setItem('fontScale', newVal.toString())
  })

  watch(recoveryCode, (newVal) => {
    if (newVal) {
      errorMessage.value = false
    }
  })

  const loading = ref(false)

  const store = useStore()
  const router = useRouter()

  const user = computed(() => store.getters.getUser)
  const formattedGanji = computed(() => {
    const g = user.value?.ganji
    if (!g) return '정보 없음'
    return `${g.yearGanji || '?'}(년), ${g.monthGanji || '?'}(월), ${g.dayGanji || '?'}(일), ${g.hourGanji || '?'}(시)`
  })
  const formattedElements = computed(() => {
    const e = user.value?.elementCounts
    if (!e) return '정보 없음'
    return `목(${e.wood || '0'}), 화(${e.fire || '0'}), 토(${e.earth || '0'}), 금(${e.metal || '0'}), 수(${e.water || '0'})`
  })

  const userLydia = computed(() => store.getters.getLydia)

  const formattedCreatedAt = computed(() => {
    if (!user.value?.createdAt) return 'N/A'
    const date = new Date(user.value.createdAt)
    return `${date.getFullYear()}년 ${date.getMonth() + 1}월 ${date.getDate()}일 ${date.getHours()}시 ${date.getMinutes()}분`
  })

  const goBack = () => {
    router.go(-1)
  }


  // 오늘의 기운 알림 토글 로직
  const pushEnabled = ref(localStorage.getItem('pushEnabled') === 'true')

  const handlePushToggle = async () => {
    try {
      if (pushEnabled.value) {
        console.log('푸시 알림 구독 시작')
        await requestPermissionAndSubscribe()
        localStorage.setItem('pushEnabled', 'true')
      } else {
        console.log('푸시 알림 구독 해제')
        await unsubscribeFromTopic()
        localStorage.setItem('pushEnabled', 'false')
      }
    } catch (err) {
      console.error('푸시 설정 변경 실패:', err)
    }
  }


  const recoverAccount = async () => {
    errorMessage.value = ''
    successMessage.value = ''
    loading.value = true

    if (!recoveryCode.value) {
      errorMessage.value = '복구코드를 입력하세요.'
      loading.value = false
      return
    }

    try {
      const guestAccessToken = localStorage.getItem('accessToken')

      const usersRef = collection(db, 'users')
      const q = query(usersRef, where('recoveryCode', '==', recoveryCode.value))
      const querySnapshot = await getDocs(q)

      if (querySnapshot.empty) {
        errorMessage.value = '유효하지 않은 복구코드입니다.'
      } else {
        const userDoc = querySnapshot.docs[0]
        const userData = userDoc.data()
        const accessToken = userDoc.id // 문서 ID가 액세스 코드입니다.

        console.log('복구 성공! 사용자 데이터:', userData)

        // localStorage에 액세스 코드 저장
        localStorage.setItem('accessToken', accessToken)

        // Vuex store에 사용자 정보 업데이트 (initializeAuth 액션 호출 또는 직접 commit)
        // initializeAuth 액션을 호출하여 전체 로딩 및 설정 프로세스를 따르는 것이 일관적입니다.
        await store.dispatch('initializeAuth')

        successMessage.value = '계정이 성공적으로 복구되었습니다.'

        if (guestAccessToken && guestAccessToken !== accessToken) {
          try {
            await deleteDoc(doc(db, 'users', guestAccessToken))
            console.log('손님 사용자 문서 삭제 완료')
          } catch (e) {
            console.warn('손님 사용자 문서 삭제 실패:', e)
          }
        }

        // 메인 화면으로 리다이렉트 (딜레이 추가)
        setTimeout(() => {
          router.replace('/IChingDaily') // 메인 화면 경로
        }, 2000) // 성공 메시지를 볼 시간 제공
      }
    } catch (error) {
      console.error('계정 복구 중 오류 발생:', error)
      errorMessage.value =
        '계정 복구 중 오류가 발생했습니다. 다시 시도해주세요.'
    } finally {
      loading.value = false
    }
  }
</script>

<style scoped>
  .container {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    max-width: 480px;
    margin: 0 auto;
    padding: 1rem;
  }

  .user-info-box p {
    margin-bottom: 0.5rem;
    flex-grow: 1;
  }

  .footer {
    margin-top: auto;
    display: flex;
    justify-content: center;
  }

  .back-button.danger {
    background-color: #e53935;
    color: white;
    border: none;
    padding: 0.6rem 1.2rem;
    border-radius: 6px;
    font-size: var(--font-size-095);
    cursor: pointer;
  }

  .recovery-box {
    margin-top: 1rem;
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .recovery-box input {
    flex: 1 1 140px; /* grow, shrink 가능, 최소 140px */
    min-width: 0; /* 오버플로우 방지 */
    box-sizing: border-box;
    padding: 0.5rem;
    padding: 0.5rem;
    border: 1px solid #ccc;
    border-radius: 4px;
    font-size: var(--font-size-10);
  }

  .recovery-box button {
    flex: 0 0 auto; /* 줄바꿈은 가능하지만 늘어나지 않음 */
    white-space: nowrap; /* 텍스트 줄바꿈 방지 */
    padding: 0.5rem 1rem;
    background-color: #4caf50;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: var(--font-size-10);
  }

  .recovery-box button:hover {
    background-color: #45a049;
  }

  .message-area {
    height: 20px; /* 항상 공간 확보 */
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    margin-top: 5px;
    margin-bottom: 10px;
    overflow-x: hidden; /* 내용이 넘칠 경우 숨김 처리 */
  }

  .message-area p {
    margin: 0;
    padding: 0;
    line-height: 1.2;
  }

  .error-message {
    color: red;
    font-size: var(--font-size-095);
  }

  .success-message {
    font-size: var(--font-size-095);
    color: #45a049; /* 성공 메시지 색상 */
  }

  .settings-box {
    margin-top: 1.5rem;
    padding-top: 0.5rem;
  }

  .font-size-slider {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
  }

  .font-size-slider input[type='range'] {
    width: 100%;
    accent-color: #1565c0;
  }


  /* 라벨 왼쪽 / 체크박스 오른쪽 정렬 */
  .push-toggle {
    margin-top: 1.5rem;
  }

  .push-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
  }

  .push-header label {
    flex: 1 1 auto;
    font-size: var(--font-size-10);
  }

  /* 체크박스 색상 (진한 파란색) */
  .push-header input[type='checkbox'] {
    width: 40px;
    height: 22px;
    cursor: pointer;
    accent-color: #1565c0; /* 또는 #1565c0 */
  }

  /* 아래 안내 문구 스타일 */
  .push-hint {
    margin: 6px 0 0;
    font-size: var(--font-size-09);
    color: #6b7280; /* 중간 회색 (tailwind gray-500 느낌) */
    line-height: 1.35;
  }

</style>
